import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { savedRevenueOverride } from '../pages/AOP/sowRevenue';

const initialState = {
  data: [],
  rawPayload: null,
  leverMap: null,
  overrides: {},
  changedCells: {},
  backgroundCalculated: {},
  loading: false,
  downloading: false,
  error: null,
  uploadMessage: '',
  predictedData: [],
  activityLogs: [],
  clientInsights: [],
  // NEW: State to hold fetched AOP Comparison comments
  aopComments: [],
  // Step-by-step calculation log returned by the engine (same call as before, just kept now)
  debugFlow: [],
  // Result of the silent "calculated vs predicted" check (separate from `data`)
  validation: {
    status: 'idle',
    calculatedRows: [],
    debugFlow: [],
    error: null,
  },
};

// 1. PURE FETCH THUNK: Gets raw payloads from the DB if needed
export const fetchAopActuals = createAsyncThunk(
  'aop/fetchAopActuals',
  // Removed accountId, relying purely on accountName
  async (
    { accountName, forceDbRefresh = false },
    { getState, rejectWithValue },
  ) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const { aop } = getState();

      if (aop.rawPayload && !forceDbRefresh) {
        return {
          rawPayload: aop.rawPayload,
          leverMap: aop.leverMap,
        };
      }

      // Send the account properties as query parameters
      const actualsResponse = await axios.get(
        `${config.apiURL}/api/v1/aop-actuals`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { accountName },
        },
      );

      const rawPayload = actualsResponse.data?.data || [];
      if (rawPayload.length === 0)
        throw new Error('No data returned from Database');

      const leverMap = {};
      rawPayload.forEach((row) => {
        if (row.LeverBaseId) leverMap[row.LeverBaseId] = row.LeverBaseId;
      });

      return { rawPayload, leverMap };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed fetching AOP Actuals');
    }
  },
);

// 2. PURE CALCULATION THUNK: Runs engine on existing state payloads
export const calculateProjections = createAsyncThunk(
  'aop/calculateProjections',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const { aop } = getState();

      const isSaveOp = !!options.saveToDb;
      const isHardRefresh = !!options.isHardRefresh;
      const skipPredictions = !!options.skipPredictions;
      // validateOnly: run the same engine call, but only keep the result for the
      // calculated-vs-predicted check; the grid data / loading state are left alone.
      const validateOnly = !!options.validateOnly;

      if (!aop.rawPayload || !aop.leverMap) {
        throw new Error('No raw payload available to execute calculations.');
      }

      // ---------------------------------------------------------
      // CORE FIX: Clear overrides on hard refresh, otherwise merge activity logs
      // ---------------------------------------------------------
      let mergedOverrides = {};

      if (isHardRefresh) {
        // Force overrides to be completely empty for a pure engine recalculation
        mergedOverrides = {};
      } else {
        // Normal mode: deep copy local overrides from state
        mergedOverrides = JSON.parse(JSON.stringify(aop.overrides || {}));

        let rawLogsArray = [];
        if (Array.isArray(aop.activityLogs)) {
          rawLogsArray = aop.activityLogs;
        } else if (aop.activityLogs && Array.isArray(aop.activityLogs.data)) {
          rawLogsArray = aop.activityLogs.data;
        } else if (
          aop.activityLogs &&
          aop.activityLogs.data &&
          Array.isArray(aop.activityLogs.data.data)
        ) {
          rawLogsArray = aop.activityLogs.data.data;
        }

        if (rawLogsArray.length > 0) {
          const sortedLogs = [...rawLogsArray].sort((a, b) => {
            const timeA = new Date(
              a.ACTIVITY_TIMESTAMP || a.activityTimestamp || a.CREATED_AT || 0,
            ).getTime();
            const timeB = new Date(
              b.ACTIVITY_TIMESTAMP || b.activityTimestamp || b.CREATED_AT || 0,
            ).getTime();
            return timeA - timeB;
          });

          sortedLogs.forEach((log) => {
            const leverId =
              log.USER_TAG ||
              log.userTag ||
              log.User_tag ||
              log.LEVER_BASE_ID ||
              log.leverBaseId;
            const month =
              log.MONTHS_CATEGORY ||
              log.MONTH_CATEGORY ||
              log.monthCategory ||
              log.Month_category ||
              log.MONTH ||
              log.month;
            const valueStr =
              log.UPDATED_VALUE ||
              log.updatedValue ||
              log.Updated_value ||
              log.VALUE ||
              log.value;

            if (leverId && month && valueStr !== undefined) {
              const numVal = parseFloat(valueStr);

              if (!isNaN(numVal)) {
                if (!mergedOverrides[leverId]) {
                  mergedOverrides[leverId] = {};
                }
                // Only apply historical log if the current user hasn't made an unsaved local edit to this cell
                if (aop.overrides[leverId]?.[month] === undefined) {
                  mergedOverrides[leverId][month] = numVal;
                }
              }
            }
          });
        }
      }
      // Revenue is not an input: the saved value is used (also on a hard refresh), so an
      // account without SOW / Pipeline rows keeps it; the server replaces it with the
      // SOW / Pipeline sums when the account has them.
      const savedRevenue = savedRevenueOverride(aop.predictedData, aop.rawPayload);
      if (savedRevenue) {
        mergedOverrides[savedRevenue.leverId] = {
          ...(mergedOverrides[savedRevenue.leverId] || {}),
          ...savedRevenue.values,
        };
      }
      // ---------------------------------------------------------

      const requestPayload = {
        x: aop.rawPayload,
        leverMap: aop.leverMap,
        overrides: mergedOverrides,
        testMode: true,
        exportCsv: false,
        saveToDb: isSaveOp, // Kept for backwards compatibility, but not primarily used for saving anymore
      };

      console.log('🚀 PAYLOAD SENDING TO ENGINE:', requestPayload);

      const calcResponse = await axios.post(
        `${config.apiURL}/api/v1/calculate-projections`,
        requestPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      let nextCalculatedData = calcResponse.data?.data || [];

      // STRIP OUT PREDICTIONS IF FLAG IS TRUE
      if (skipPredictions && aop.rawPayload && aop.rawPayload.length > 0) {
        const actualKeys = Object.keys(aop.rawPayload[0]);

        nextCalculatedData = nextCalculatedData.map((row) => {
          const rowCopy = { ...row };
          Object.keys(rowCopy).forEach((key) => {
            if (
              /^[A-Z][a-z]{2}-\d{2}$/.test(key) &&
              !actualKeys.includes(key)
            ) {
              rowCopy[key] = '';
            }
          });
          return rowCopy;
        });
      }

      const debugFlow = calcResponse.data?.debugFlow || [];

      if (validateOnly) {
        return { data: nextCalculatedData, changedCells: {}, debugFlow };
      }

      const deltaMap = {};

      if (aop.data.length > 0 && !isHardRefresh) {
        nextCalculatedData.forEach((newRow) => {
          const oldRow = aop.data.find(
            (r) => r.LeverBaseId === newRow.LeverBaseId,
          );
          if (oldRow) {
            Object.keys(newRow).forEach((key) => {
              if (/^[A-Z][a-z]{2}-\d{2}$/.test(key)) {
                const oldNum = parseFloat(oldRow[key]) || 0;
                const newNum = parseFloat(newRow[key]) || 0;
                if (Math.abs(newNum - oldNum) > 0.000001) {
                  if (!deltaMap[newRow.LeverBaseId])
                    deltaMap[newRow.LeverBaseId] = {};
                  deltaMap[newRow.LeverBaseId][key] = true;
                }
              }
            });
          }
        });
      }

      return {
        data: nextCalculatedData,
        changedCells: deltaMap,
        debugFlow,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Calculation engine failed',
      );
    }
  },
);

export const fetchAopPredicted = createAsyncThunk(
  'aop/fetchAopPredicted',
  // Removed accountId, relying purely on accountName
  async ({ accountName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/api/v1/aop-predicted`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { accountName },
        },
      );

      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch predicted data',
      );
    }
  },
);

export const downloadProjectionsCsv = createAsyncThunk(
  'aop/downloadProjectionsCsv',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const { aop } = getState();

      if (!aop.rawPayload || !aop.leverMap)
        throw new Error('Please load data before exporting');

      const response = await axios.post(
        `${config.apiURL}/api/v1/calculate-projections`,
        {
          x: aop.rawPayload,
          leverMap: aop.leverMap,
          overrides: aop.overrides,
          testMode: true,
          exportCsv: true,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'projections.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      return rejectWithValue(error.message || 'CSV Export Failed');
    }
  },
);

export const fetchActivityLogs = createAsyncThunk(
  'aop/fetchActivityLogs',
  // Replaced accountId with accountName for fetching logs
  async (accountId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/api/v1/aop-activity/${accountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch logs',
      );
    }
  },
);

export const saveActivityLog = createAsyncThunk(
  'aop/saveActivityLog',
  async (activityData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      await axios.post(
        `${config.apiURL}/api/v1/aop-activity-save`,
        activityData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return { success: true };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save activity',
      );
    }
  },
);

// ============================================================================
// Fetch Client Insights (Passes email in headers)
// ============================================================================
export const fetchClientInsights = createAsyncThunk(
  'aop/fetchClientInsights',
  // arg: the e-mail, or { email, fresh: true } to bypass the server's short-lived copy
  async (arg, { rejectWithValue }) => {
    const email = typeof arg === 'string' ? arg : arg?.email;
    const fresh = typeof arg === 'object' && arg?.fresh;
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/api/v1/client-insights`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            useremail: email, // Passes to your controller's req.headers.useremail
          },
          // Always a fresh answer: the RLS list changes, and a cached reply is what
          // used to need Ctrl+Shift+R
          params: fresh ? { _: Date.now(), fresh: 1 } : { _: Date.now() },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch client insights',
      );
    }
  },
);

// ============================================================================
// Bulk Save Grid Data (Direct to DB, Bypassing Engine)
// ============================================================================
export const saveFullGridData = createAsyncThunk(
  'aop/saveFullGridData',
  async (fullPayloadArray, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');

      const response = await axios.post(
        `${config.apiURL}/api/v1/aop-predicted-save`, // Hits our new directSaveController!
        fullPayloadArray,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to directly save grid data',
      );
    }
  },
);

// ============================================================================
// NEW THUNK: Fetch AOP Comments
// ============================================================================
export const fetchAopComments = createAsyncThunk(
  'aop/fetchAopComments',
  async (accountName, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      const response = await axios.get(
        `${config.apiURL}/api/v1/aop-comments/${accountName}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data?.data || response.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch AOP comments',
      );
    }
  },
);

// ============================================================================
// NEW THUNK: Save AOP Comment
// ============================================================================
export const saveAopComment = createAsyncThunk(
  'aop/saveAopComment',
  async (commentData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');
      await axios.post(`${config.apiURL}/api/v1/aop-comments`, commentData, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      // Optional: You could dispatch fetchAopComments here to refresh the list,
      // or return the data to optimistically update the state.
      return { success: true, commentData };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save AOP comment',
      );
    }
  },
);

const aopSlice = createSlice({
  name: 'aop',
  initialState,
  reducers: {
    applyCellOverride: (state, action) => {
      const { leverId, month, numVal } = action.payload;
      if (!state.overrides[leverId]) state.overrides[leverId] = {};
      state.overrides[leverId][month] = numVal;
      state.data = state.data.map((row) =>
        row.LeverBaseId === leverId ? { ...row, [month]: numVal } : row,
      );
    },
    // client list restored from the browser while the fresh one is still loading
    setClientInsights: (state, action) => {
      state.clientInsights = action.payload;
    },
    clearCellOverride: (state, action) => {
      const { leverId, month } = action.payload;
      if (state.overrides[leverId]) {
        delete state.overrides[leverId][month];
        if (Object.keys(state.overrides[leverId]).length === 0)
          delete state.overrides[leverId];
      }
    },

    // 👇 CORE FIX APPLIED HERE 👇
    resetAopState: (state) => {
      return {
        ...initialState, // Wipe everything back to initial (data, grid, etc.)
        clientInsights: state.clientInsights, // Retain the fetched insights so dropdowns stay populated!
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAopActuals.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadMessage = 'Fetching layout matrices from Database...';
      })
      .addCase(fetchAopActuals.fulfilled, (state, action) => {
        state.loading = false;
        state.rawPayload = action.payload.rawPayload;
        state.leverMap = action.payload.leverMap;
        state.data = action.payload.rawPayload;
        state.uploadMessage = 'Data fetched successfully.';
      })
      .addCase(fetchAopActuals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadMessage = action.payload;
      })
      .addCase(calculateProjections.pending, (state, action) => {
        if (action.meta.arg?.validateOnly) {
          state.validation = {
            ...state.validation,
            status: 'running',
            error: null,
          };
          return;
        }
        state.loading = true;
        state.error = null;
        state.uploadMessage = 'Processing financial matrix engine loop...';
      })
      .addCase(calculateProjections.fulfilled, (state, action) => {
        if (action.meta.arg?.validateOnly) {
          state.validation = {
            status: 'done',
            calculatedRows: action.payload.data,
            debugFlow: action.payload.debugFlow,
            error: null,
          };
          return;
        }
        state.loading = false;
        state.data = action.payload.data;
        state.changedCells = action.payload.changedCells;
        state.debugFlow = action.payload.debugFlow || [];
        state.uploadMessage = 'Calculations completed successfully!';
      })
      .addCase(calculateProjections.rejected, (state, action) => {
        if (action.meta.arg?.validateOnly) {
          state.validation = {
            status: 'error',
            calculatedRows: [],
            debugFlow: [],
            error: action.payload || 'Calculation check failed',
          };
          return;
        }
        state.loading = false;
        state.error = action.payload;
        state.uploadMessage = action.payload;
      })
      .addCase(downloadProjectionsCsv.pending, (state) => {
        state.downloading = true;
      })
      .addCase(downloadProjectionsCsv.fulfilled, (state) => {
        state.downloading = false;
      })
      .addCase(downloadProjectionsCsv.rejected, (state) => {
        state.downloading = false;
      })
      .addCase(fetchActivityLogs.fulfilled, (state, action) => {
        state.activityLogs = action.payload;
      })
      .addCase(fetchAopPredicted.fulfilled, (state, action) => {
        state.predictedData = action.payload;
      })

      // ============================================================================
      // REDUCERS: Handle Client Insights States
      // ============================================================================
      // The client list loads in the background (and may be refreshed while the grid is
      // open), so it must not switch the grid's global `loading` flag on and off.
      .addCase(fetchClientInsights.fulfilled, (state, action) => {
        state.clientInsights = action.payload;
      })
      .addCase(fetchClientInsights.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ============================================================================
      // REDUCERS: Handle the Bulk Save States
      // ============================================================================
      .addCase(saveFullGridData.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadMessage = 'Saving direct grid overrides to Database...';
      })
      .addCase(saveFullGridData.fulfilled, (state) => {
        state.loading = false;
        state.uploadMessage = 'Grid overrides saved successfully.';
      })
      .addCase(saveFullGridData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadMessage = action.payload;
      })

      // ============================================================================
      // NEW REDUCERS: Handle AOP Comments states
      // ============================================================================
      // Comments load in the background after the grid is shown; they must not switch the
      // grid's global `loading` flag (that produced a second loading overlay).
      .addCase(fetchAopComments.fulfilled, (state, action) => {
        state.aopComments = action.payload;
      })
      .addCase(saveAopComment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadMessage = 'Saving comment...';
      })
      // Inside aopDataReducer.js -> extraReducers
      .addCase(saveAopComment.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadMessage = 'Comment saved successfully.';

        // Optimistically update the state, matching Database UPPERCASE column names
        if (action.payload.commentData) {
          const { accountName, leverKey, leverSubKey, actualsMonth, comments } =
            action.payload.commentData;

          // Remove any existing comment for this cell to avoid duplicates in the UI array
          state.aopComments = state.aopComments.filter(
            (c) =>
              !(
                c.LEVERKEY === leverKey &&
                c.LEVERSUBKEY === leverSubKey &&
                new Date(c.ACTUALS_MONTH).getTime() ===
                  new Date(actualsMonth).getTime()
              ),
          );

          // Push the new comment with UPPERCASE keys
          state.aopComments.push({
            ACCOUNT_NAME: accountName,
            LEVERKEY: leverKey,
            LEVERSUBKEY: leverSubKey,
            ACTUALS_MONTH: actualsMonth,
            COMMENTS: comments,
          });
        }
      })
      .addCase(saveAopComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadMessage = action.payload;
      });
  },
});

export const {
  applyCellOverride,
  clearCellOverride,
  resetAopState,
  setClientInsights,
} = aopSlice.actions;
export default aopSlice.reducer;
